import React from 'react';
import styled from 'styled-components';
import {
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignCenterVertical,
  AlignStartVertical,
  AlignEndVertical
} from 'lucide-react';
import { Copy, FlipHorizontal2, FlipVertical2, BringToFront, Trash2, Minus, Plus, MoveHorizontal, ArrowUpDown, ChevronDown } from 'lucide-react';
import { useDesignContext } from '../hooks/useDesignContext';

const AlignmentContainer = styled.div<{ $left?: number | string; $top?: number | string; $right?: number | string; orientation?: 'horizontal' | 'vertical' }>`
  position: absolute;
  top: ${props => (typeof props.$top === 'number' ? `${props.$top}px` : (props.$top || '80px'))};
  left: ${props => (typeof props.$left === 'number' ? `${props.$left}px` : (typeof props.$right !== 'undefined' ? 'auto' : '50%'))};
  right: ${props => (typeof props.$right === 'number' ? `${props.$right}px` : 'auto')};
  transform: ${props => {
    let t = '';
    // Horizontal centering if no specific left/right
    if (typeof props.$right === 'undefined' && typeof props.$left === 'undefined') {
      t += 'translateX(-50%) ';
    }
    // Vertical centering
    if (props.$top === '50%') {
      t += 'translateY(-50%) ';
    }
    return t.trim() || 'none';
  }};
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.98) 100%);
  border: 1px solid rgba(226, 232, 240, 0.8);
  border-radius: ${props => props.orientation === 'vertical' ? '12px' : '10px'};
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08);
  padding: ${props => props.orientation === 'vertical' ? '12px 8px' : '10px 12px'};
  display: flex;
  flex-direction: ${props => props.orientation === 'vertical' ? 'column' : 'row'};
  gap: ${props => props.orientation === 'vertical' ? '8px' : '6px'};
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(12px);
  width: max-content;
  max-width: ${props => props.orientation === 'vertical' ? 'none' : '95vw'};
  max-height: ${props => props.orientation === 'vertical' ? '80vh' : 'auto'};
  overflow-x: ${props => props.orientation === 'vertical' ? 'hidden' : 'auto'};
  overflow-y: ${props => props.orientation === 'vertical' ? 'auto' : 'hidden'};
  
  &::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(148, 163, 184, 0.3);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(148, 163, 184, 0.5);
  }

  @media (max-width: 768px) {
    position: fixed !important;
    top: auto !important;
    bottom: 0 !important;
    left: 0 !important;
    right: 0 !important;
    transform: none !important;
    width: 100%;
    max-width: 100%;
    flex-direction: row;
    border-radius: 16px 16px 0 0;
    border: none;
    border-top: 1px solid #e2e8f0;
    box-shadow: 0 -4px 20px rgba(0,0,0,0.15);
    padding: 10px 12px calc(10px + env(safe-area-inset-bottom)) 12px;
    z-index: 2000;
  }
`;

const ControlGroup = styled.div<{ orientation?: 'horizontal' | 'vertical' }>`
  display: flex;
  gap: 4px;
  align-items: center;
  padding: ${props => props.orientation === 'vertical' ? '6px 0' : '0 8px'};
  flex-direction: ${props => props.orientation === 'vertical' ? 'column' : 'row'};
  
  &:not(:last-child) {
    ${props => props.orientation === 'vertical' ? `
      border-bottom: 1px solid rgba(226, 232, 240, 0.6);
      padding-bottom: 8px;
      margin-bottom: 4px;
      border-right: none;
      padding-right: 0;
      margin-right: 0;
    ` : `
      border-right: 1px solid rgba(226, 232, 240, 0.6);
      padding-right: 12px;
      margin-right: 4px;
    `}
  }
`;

const FontDisplay = styled.div`
  display: flex;
  align-items: center;
  height: 36px;
  padding: 0 10px;
  font-size: 12px;
  font-weight: 600;
  color: #334155;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  white-space: nowrap;
  min-width: 110px;
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: default;
  user-select: none;
  letter-spacing: 0.01em;
`;

const StyledInput = styled.input`
  height: 36px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 0 8px;
  font-size: 13px;
  background-color: #f8fafc;
  color: #334155;
  outline: none;
  text-align: center;
  transition: all 0.2s;
  width: 60px;

  &:hover {
    border-color: #cbd5e1;
    background-color: #fff;
  }
  &:focus {
    border-color: #cbd5e1;
  }
`;

const StyledSelect = styled.select`
  height: 36px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 0 4px 0 8px;
  font-size: 13px;
  background-color: #f8fafc;
  color: #334155;
  outline: none;
  transition: all 0.2s;
  width: 140px;
  cursor: pointer;

  &:hover {
    border-color: #cbd5e1;
    background-color: #fff;
  }
  
  &:focus {
    border-color: #3b82f6;
    background-color: #fff;
  }
`;

const ToggleButton = styled.button<{ active?: boolean }>`
  height: 36px;
  padding: 0 12px;
  font-size: 13px;
  font-weight: 500;
  border: 1px solid ${props => props.active ? 'transparent' : '#e2e8f0'};
  border-radius: 6px;
  background-color: ${props => props.active ? '#3b82f6' : '#fff'};
  color: ${props => props.active ? '#fff' : '#475569'};
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background-color: ${props => props.active ? '#2563eb' : '#f1f5f9'};
  }
`;

const NumberControlButton = styled.button`
  width: 32px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e2e8f0;
  background-color: #fff;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: #f8fafc;
    color: #334155;
  }
  
  &:first-child {
    border-top-left-radius: 6px;
    border-bottom-left-radius: 6px;
    border-right: none;
  }
  &:last-child {
    border-top-right-radius: 6px;
    border-bottom-right-radius: 6px;
    border-left: none;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const NumberControlInput = styled.input`
  width: 56px;
  height: 36px;
  border: 1px solid #e2e8f0;
  text-align: center;
  font-size: 13px;
  color: #334155;
  outline: none;
  background: white;
  
  &:focus {
    border-color: #cbd5e1;
    z-index: 1;
  }
`;

const AlignmentButton = styled.button<{ active?: boolean }>`
  min-width: 36px;
  height: 38px;
  padding: 0 10px;
  border: 1px solid ${props => props.active ? '#3b82f6' : 'rgba(226, 232, 240, 0.6)'};
  background: ${props => props.active ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'};
  color: ${props => props.active ? '#ffffff' : '#475569'};
  border-radius: 7px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.01em;
  position: relative;
  white-space: nowrap;
  box-shadow: ${props => props.active ? '0 2px 8px rgba(59, 130, 246, 0.25)' : '0 1px 3px rgba(0, 0, 0, 0.05)'};
  
  &:hover {
    background: ${props => props.active ? 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)' : 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)'};
    border-color: ${props => props.active ? '#2563eb' : '#cbd5e1'};
    box-shadow: ${props => props.active ? '0 4px 12px rgba(59, 130, 246, 0.3)' : '0 2px 6px rgba(0, 0, 0, 0.08)'};
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: ${props => props.active ? '0 1px 4px rgba(59, 130, 246, 0.2)' : '0 1px 2px rgba(0, 0, 0, 0.05)'};
  }
  
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  
  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    opacity: ${props => props.active ? 1 : 0.85};
  }
  
  span {
    font-size: 10.5px;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }
`;

const Divider = styled.div`
  width: 1px;
  height: 20px;
  background: #d1d5db;
  margin: 0 4px;
  align-self: center;
`;

interface AlignmentToolbarProps {
  selectedElement: any;
  selectedItemIds: string[];
  onAlign: (alignment: 'left' | 'center' | 'right' | 'top' | 'middle' | 'bottom') => void;
  stageSize: { width: number; height: number };
  position?: { left?: number | string; top?: number | string; right?: number | string } | null;
  orientation?: 'horizontal' | 'vertical';
  onCopy?: () => void;
  onMirror?: () => void;
  onFlip?: () => void;
  onBringToFront?: () => void;
  canMirrorFlip?: boolean;
  onDelete?: () => void;
  // Vase-specific copy behavior
  isVase?: boolean;
  canCopyVase?: boolean;
  // Compact text controls
  textControls?: {
    fontFamily: string;
    fontSize: number;
    lineSpacing: number;
    kerning: number;
    isVCut?: boolean;
    isFrost?: boolean;
    isPolish?: boolean;
    isOutline?: boolean;
    onChange: (next: { fontFamily?: string; fontSize?: number; lineSpacing?: number; kerning?: number }) => void;
    isShape?: boolean;
    onToggleVCut?: () => void;
    onToggleFrost?: () => void;
    onTogglePolish?: () => void;
    onToggleOutline?: () => void;
    onToggleShape?: () => void;
  } | null;
}

const AlignmentToolbar: React.FC<AlignmentToolbarProps> = ({
  selectedElement,
  selectedItemIds,
  onAlign,
  stageSize,
  position,
  orientation = 'horizontal', // Default to horizontal if not specified
  onCopy,
  onMirror,
  onFlip,
  onBringToFront,
  canMirrorFlip = true,
  onDelete,
  isVase = false,
  canCopyVase = true,
  textControls
}) => {
  const { sharedFonts, textStyles, isLoadingTextStyles, currentDesign, sharedColors } = useDesignContext();

  const activeColorObj = React.useMemo(() => {
    return (sharedColors || []).find((c: any) => Number(c.id) === Number(currentDesign?.selectedColorId));
  }, [sharedColors, currentDesign?.selectedColorId]);

  const isPJB = React.useMemo(() => {
    const code = String(activeColorObj?.code || '').toUpperCase();
    const name = String(activeColorObj?.name || '').toUpperCase();
    const id = Number(currentDesign?.selectedColorId || 0);
    return code === 'PJB' || name.includes('BLACK') || id === 5 || id === 14;
  }, [activeColorObj, currentDesign?.selectedColorId]);

  const elementId = (selectedElement?.id || '').toLowerCase().replace('-back', '');
  const fieldNameLc = String(selectedElement?.properties?.fieldName || '').toLowerCase();
  const isVerseForFont = elementId === 'verse' || elementId.includes('esq-txt-verse') || fieldNameLc === 'verse';

  // Determine size logic (increments and limits) based on field type from DB
  const sizeLogic = React.useMemo(() => {
    const id = (selectedElement?.id || '').toLowerCase().replace('-back', '');
    const isVerse = id.includes('verse') || id.includes('additionaltext') || id.includes('wedding');
    const step = isVerse ? 0.125 : 0.25;

    // min/max come exclusively from the DB — no hardcoded fallbacks
    let min: number | null = null;
    let max: number | null = null;

    if (textStyles && !isLoadingTextStyles) {
      let style = null;
      if (id.includes('firstname')) style = textStyles.first_name;
      else if (id.includes('middlename')) style = textStyles.middle_name;
      else if (id.includes('lastname')) style = textStyles.last_name;
      else if (id.includes('dob') || id.includes('dod') || id.includes('wedding')) style = textStyles.date;
      else if (id.includes('verse')) style = textStyles.verse;
      else if (id.includes('additionaltext')) style = textStyles.additional_text;

      if (style) {
        min = parseFloat(style.min_size_inches);
        if (id.includes('verse')) min = Math.min(min, 0.875);
        if (id.includes('wedding')) {
          min = 0.875;
          max = 0.875;
        } else {
          max = parseFloat(style.max_size_inches);
        }
      }
    }

    return { step, min, max };
  }, [selectedElement, textStyles, isLoadingTextStyles]);

  // Grid-aware increment: always move to the next multiple of step above current value
  const snapUp = (val: number, step: number, max: number | null): number => {
    const next = Number((Math.ceil(val / step + 0.001) * step).toFixed(3));
    return max !== null ? Math.min(next, max) : next;
  };

  // Grid-aware decrement: always move to the next multiple of step below current value
  const snapDown = (val: number, step: number, min: number | null): number => {
    const prev = Number((Math.floor(val / step - 0.001) * step).toFixed(3));
    return min !== null ? Math.max(prev, min) : prev;
  };

  // Snap an arbitrary typed value to the nearest grid point within DB bounds
  const snapToGrid = (val: number, step: number, min: number | null, max: number | null): number => {
    const snapped = Number((Math.round(val / step) * step).toFixed(3));
    let result = snapped;
    if (min !== null) result = Math.max(result, min);
    if (max !== null) result = Math.min(result, max);
    return result;
  };
  const hasSelection = selectedElement || selectedItemIds.length > 0;
  if (!hasSelection) return null;

  const handleAlignment = (alignment: 'left' | 'center' | 'right' | 'top' | 'middle' | 'bottom') => {
    onAlign(alignment);
  };

  const left = position?.left;
  const top = position?.top;
  const right = position?.right;

  return (
    <AlignmentContainer
      id="alignment-toolbar-container"
      $left={left}
      $top={top}
      $right={right}
      orientation={orientation}
    >
      {/* Selection count indicator */}
      {selectedItemIds.length > 1 && (
        <div style={{
          padding: '2px 8px',
          fontSize: '11px',
          fontWeight: '600',
          color: '#374151',
          background: '#f3f4f6',
          borderRadius: '4px',
          marginRight: orientation === 'vertical' ? '0' : '8px',
          marginBottom: orientation === 'vertical' ? '8px' : '0'
        }}>
          {selectedItemIds.length} sel
        </div>
      )}

      {/* VASE MODE: Copy & Delete Only */}
      {isVase ? (
        <ControlGroup orientation={orientation} style={{ border: 'none', paddingRight: 0, marginRight: 0, paddingBottom: 0, marginBottom: 0 }}>
          <AlignmentButton
            onClick={onCopy}
            title="Copy to opposite side"
            disabled={!canCopyVase}
          >
            <Copy size={18} />
            <span>Copy</span>
          </AlignmentButton>
          <AlignmentButton onClick={onDelete} title="Delete">
            <Trash2 size={18} />
            <span>Delete</span>
          </AlignmentButton>
        </ControlGroup>
      ) : textControls ? ( // TEXT MODE: Text Controls + Delete Only (Alignment included as it is "important")
        <>
          <ControlGroup orientation={orientation}>
            <div style={{ display: 'flex', alignItems: 'center', flexDirection: orientation === 'vertical' ? 'column' : 'row', gap: orientation === 'vertical' ? 4 : 8 }} title={isVerseForFont ? "Select Verse Font" : "Font (from design style — set in model_size_text_styles)"}>
              {orientation === 'vertical' && <span style={{ fontSize: '10px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Font</span>}
              {isVerseForFont ? (
                <StyledSelect
                  value={textControls.fontFamily}
                  onChange={(e) => textControls.onChange({ fontFamily: e.target.value })}
                  title="Select Font"
                >
                  <option value="Vermarco">Vermarco</option>
                  {isPJB && <option value="MonotypeCorsiva">Monotype Corsiva</option>}
                  {isPJB && <option value="PamegaScript">Pamega Script</option>}
                  {textControls.fontFamily && !['Vermarco', 'MonotypeCorsiva', 'PamegaScript'].includes(textControls.fontFamily) && (
                    <option value={textControls.fontFamily}>
                      {sharedFonts.find((f: any) => f.family === textControls.fontFamily)?.name || textControls.fontFamily}
                    </option>
                  )}
                  {sharedFonts.map((f: any) => {
                    if (['Vermarco', 'MonotypeCorsiva', 'PamegaScript', textControls.fontFamily].includes(f.family)) return null;
                    if (!isPJB && ['MonotypeCorsiva', 'PamegaScript'].includes(f.family)) return null;
                    return <option key={f.family} value={f.family}>{f.name || f.family}</option>;
                  })}
                </StyledSelect>
              ) : (
                <FontDisplay title={textControls.fontFamily || 'Default'}>
                  {sharedFonts.find((f: any) => f.family === textControls.fontFamily)?.name
                    || textControls.fontFamily
                    || 'Default'}
                </FontDisplay>
              )}
            </div>
          </ControlGroup>

          <ControlGroup orientation={orientation}>
            <div style={{ display: 'flex', alignItems: 'center', flexDirection: orientation === 'vertical' ? 'column' : 'row', gap: orientation === 'vertical' ? 4 : 8 }} title="Font Size (inches)">
              {orientation === 'vertical' && <span style={{ fontSize: '10px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Size</span>}
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <NumberControlButton
                  onClick={() => {
                    const current = textControls.fontSize || (sizeLogic.min ?? sizeLogic.step);
                    textControls.onChange({ fontSize: snapDown(current, sizeLogic.step, sizeLogic.min) });
                  }}
                  disabled={sizeLogic.min !== null && textControls.fontSize <= sizeLogic.min}
                >
                  <Minus size={16} />
                </NumberControlButton>
                <NumberControlInput
                  type="number"
                  step={sizeLogic.step}
                  min={sizeLogic.min ?? undefined}
                  max={sizeLogic.max ?? undefined}
                  value={textControls.fontSize || 0}
                  onChange={(e) => {
                    const val = Number(e.target.value || 0);
                    textControls.onChange({ fontSize: snapToGrid(val, sizeLogic.step, sizeLogic.min, sizeLogic.max) });
                  }}
                />
                <NumberControlButton
                  onClick={() => {
                    const current = textControls.fontSize || (sizeLogic.min ?? sizeLogic.step);
                    textControls.onChange({ fontSize: snapUp(current, sizeLogic.step, sizeLogic.max) });
                  }}
                  disabled={sizeLogic.max !== null && textControls.fontSize >= sizeLogic.max}
                >
                  <Plus size={16} />
                </NumberControlButton>
              </div>
            </div>
          </ControlGroup>

          <ControlGroup orientation={orientation}>
            <div style={{ display: 'flex', alignItems: 'center', flexDirection: orientation === 'vertical' ? 'column' : 'row', gap: orientation === 'vertical' ? 4 : 6 }} title="Character Spacing">
              {orientation === 'vertical' && <span style={{ fontSize: '10px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Char</span>}
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <NumberControlButton
                  onClick={() => {
                    const current = textControls.kerning ?? 2;
                    textControls.onChange({ kerning: Number((current - 0.5).toFixed(1)) });
                  }}
                >
                  <Minus size={16} />
                </NumberControlButton>
                <NumberControlInput
                  type="number"
                  step={0.5}
                  min={-10}
                  max={10}
                  value={textControls.kerning ?? 2}
                  onChange={(e) => {
                    const val = Number(e.target.value ?? 2);
                    textControls.onChange({ kerning: Math.max(-10, Math.min(10, val)) });
                  }}
                  style={{ width: 48 }}
                />
                <NumberControlButton
                  onClick={() => {
                    const current = textControls.kerning ?? 2;
                    textControls.onChange({ kerning: Number((current + 0.5).toFixed(1)) });
                  }}
                >
                  <Plus size={16} />
                </NumberControlButton>
              </div>
            </div>
          </ControlGroup>

          <ControlGroup orientation={orientation}>
            <div style={{ display: 'flex', alignItems: 'center', flexDirection: orientation === 'vertical' ? 'column' : 'row', gap: orientation === 'vertical' ? 4 : 6 }} title="Line Spacing">
              {orientation === 'vertical' && <span style={{ fontSize: '10px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Line</span>}
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <ArrowUpDown size={16} color="#64748b" />
                <StyledInput
                  type="number"
                  step={0.1}
                  min={1.0}
                  max={3}
                  value={textControls.lineSpacing || 1.1}
                  onChange={(e) => textControls.onChange({ lineSpacing: Math.max(1.0, Math.min(3, Number(e.target.value || 1.1))) })}
                  style={{ width: 56 }}
                />
              </div>
            </div>
          </ControlGroup>

          <ControlGroup orientation={orientation}>
            <AlignmentButton onClick={() => handleAlignment('left')} title="Align Left"><AlignLeft size={18} /><span>Left</span></AlignmentButton>
            <AlignmentButton onClick={() => handleAlignment('center')} title="Align Horizontal Center"><AlignCenter size={18} /><span>Center</span></AlignmentButton>
            <AlignmentButton onClick={() => handleAlignment('right')} title="Align Right"><AlignRight size={18} /><span>Right</span></AlignmentButton>
          </ControlGroup>

          <ControlGroup orientation={orientation}>
            <AlignmentButton onClick={() => handleAlignment('top')} title="Align Top"><AlignStartVertical size={18} /><span>Top</span></AlignmentButton>
            <AlignmentButton onClick={() => handleAlignment('middle')} title="Align Vertical Center"><AlignCenterVertical size={18} /><span>Middle</span></AlignmentButton>
            <AlignmentButton onClick={() => handleAlignment('bottom')} title="Align Bottom"><AlignEndVertical size={18} /><span>Bottom</span></AlignmentButton>
          </ControlGroup>

          <ControlGroup orientation={orientation}>
            <AlignmentButton onClick={onBringToFront} title="Bring To Front"><BringToFront size={18} /><span>Front</span></AlignmentButton>
          </ControlGroup>

          <AlignmentButton onClick={onDelete} title="Delete"><Trash2 size={18} style={{ color: '#ef4444' }} /><span>Delete</span></AlignmentButton>
        </>
      ) : (selectedElement?.properties?.isFixedPosition || selectedElement?.properties?.isInnerArtwork) ? ( // Restricted MODE: Delete only
        <ControlGroup orientation={orientation} style={{ border: 'none', paddingRight: 0, marginRight: 0, paddingBottom: 0, marginBottom: 0 }}>
          <AlignmentButton onClick={onDelete} title="Delete"><Trash2 size={18} style={{ color: '#ef4444' }} /><span>Delete</span></AlignmentButton>
        </ControlGroup>
      ) : ( // STANDARD/ARTWORK MODE: Alignment + Actions + Delete
        <>
          <ControlGroup orientation={orientation}>
            <AlignmentButton onClick={() => handleAlignment('left')} title="Align Left"><AlignLeft size={18} /><span>Left</span></AlignmentButton>
            <AlignmentButton onClick={() => handleAlignment('center')} title="Align Horizontal Center"><AlignCenter size={18} /><span>Center</span></AlignmentButton>
            <AlignmentButton onClick={() => handleAlignment('right')} title="Align Right"><AlignRight size={18} /><span>Right</span></AlignmentButton>
          </ControlGroup>

          <ControlGroup orientation={orientation}>
            <AlignmentButton onClick={() => handleAlignment('top')} title="Align Top"><AlignStartVertical size={18} /><span>Top</span></AlignmentButton>
            <AlignmentButton onClick={() => handleAlignment('middle')} title="Align Vertical Center"><AlignCenterVertical size={18} /><span>Middle</span></AlignmentButton>
            <AlignmentButton onClick={() => handleAlignment('bottom')} title="Align Bottom"><AlignEndVertical size={18} /><span>Bottom</span></AlignmentButton>
          </ControlGroup>

          <ControlGroup orientation={orientation} style={{ border: 'none', paddingRight: 0, marginRight: 0, paddingBottom: 0, marginBottom: 0 }}>
            <AlignmentButton
              onClick={onCopy}
              title={selectedElement?.properties?.role === 'porcelain' ? "Porcelain photos cannot be copied." : "Copy"}
              disabled={(isVase && !canCopyVase) || selectedElement?.properties?.role === 'porcelain'}
            >
              <Copy size={18} />
              <span>Copy</span>
            </AlignmentButton>
            {canMirrorFlip && selectedElement?.properties?.role !== 'porcelain' && (
              <>
                <AlignmentButton onClick={onMirror} title="Mirror"><FlipHorizontal2 size={18} /><span>Mirror</span></AlignmentButton>
                <AlignmentButton onClick={onFlip} title="Flip"><FlipVertical2 size={18} /><span>Flip</span></AlignmentButton>
              </>
            )}
            <AlignmentButton onClick={onBringToFront} title="Bring to Front"><BringToFront size={18} /><span>Front</span></AlignmentButton>
            <AlignmentButton onClick={onDelete} title="Delete"><Trash2 size={18} style={{ color: '#ef4444' }} /><span>Delete</span></AlignmentButton>
          </ControlGroup>
        </>
      )}
    </AlignmentContainer>
  );
};

export default AlignmentToolbar;
